import {
  type IUserMe,
  isLogged,
  useGetAPMs,
  useGetCandidateSchemas,
  useGetChallenges,
  useGetNotifications,
  useGetUserMe,
  useLocalStorage,
  usePostInitSettings,
} from "@api";
import type { IManagementProcess } from "@bbj-types/applyManagementProcess";
import {
  type INotification,
  isBeneficiarySocialApplyNotification,
  isPrescriptionFilledNotification,
} from "@bbj-types/notification";
import type { Override } from "@bbj-types/utils";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { setSentryUser } from "@root/utils/sentry";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleMessage } from "./api/tools/websocket/globalSubscription";
import { useWebSocket } from "./api/tools/websocket/useWebsocket";
import {
  type FilteredSettingValues,
  type FilteredSettings,
  buildQueryGlobalSettings,
  returnFilteredSettingValues,
  returnFilteredSettings,
  settingsDefinitions,
} from "./contextLogic";
import { GoogleLink, classes } from "./styles";
import { I18nProvider, defaultI18nValue } from "./utils/organisation/i18nContext";

export type TGlobalContext = Override<ReturnType<typeof useGlobalProvider>, { user: IUserMe }>;

export const Context = createContext<TGlobalContext | undefined>(undefined);

export const useGlobalContext = () => {
  const context = useContext(Context);
  if (context === undefined) {
    throw new Error("useGlobalContext must be within GlobalProvider");
  }

  return context;
};

export const useGlobalProvider = () => {
  const [, setIsLogged] = useLocalStorage("isLogged");
  const [notifications, setNotifications] = useState<INotification[]>([]);

  const navigate = useNavigate();
  const { connectWebsocket } = useWebSocket({ setNotifications });
  const [{ data: userData, loading: loadingUserMe }, getUser] = useGetUserMe();
  const [{ data: resultInitSettings }, postInitSettings] = usePostInitSettings();
  const [{ data: resultAmps }, getAmps] = useGetAPMs();
  const [{ data: resultCandidateSchemas }, getCandidateSchemas] = useGetCandidateSchemas();
  const [{ data: resultChallenges }, getChallenges] = useGetChallenges();
  const [user, setUser] = useState<IUserMe | null>(null);
  const [{ data: resultNotifications }, getNotifications] = useGetNotifications();
  const [hasHiring, setHasHiring] = useState(false);

  const reloadHorizon = useCallback(async () => {
    if (!isLogged()) {
      setIsLogged("");
      navigate("/v2/login");
    }
    try {
      if (import.meta.env.MODE === "test") {
        getChallenges(); //don't await in test
      } else {
        await getChallenges(); //await the first request to initialize authorizer
      }
    } catch (err) {
      console.error(err);
    }
    getUser();
    getAmps();
    getNotifications();
    getCandidateSchemas();
    postInitSettings(buildQueryGlobalSettings(settingsDefinitions));
  }, [getUser, getAmps, getNotifications, getCandidateSchemas, getChallenges, postInitSettings, navigate, setIsLogged]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    reloadHorizon();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (user?._id) {
      connectWebsocket(user._id);
      if (window.wsClient) {
        window.wsClient.setOnMessage(handleMessage(setHasHiring));
      }
    }
  }, [user?._id]);

  useEffect(() => {
    if (resultNotifications) {
      setNotifications(resultNotifications);
    }
  }, [resultNotifications]);

  useEffect(() => {
    if (userData) {
      setUser(userData);
      setSentryUser(userData);
      if (
        import.meta.env.VITE_GOOGLE_CONNECT === "true" &&
        (!userData.oauth2?.google || userData.oauth2?.google?.isExpired) &&
        userData.organisation.features.google?.active
      ) {
        window.publish("feedback", {
          severity: "error",
          message: (
            <Typography>
              Vous n'êtes plus connecté à votre compte Gmail. Veuillez vous rendre sur "
              <GoogleLink
                className={classes.root}
                href={`/v2/settings/user/${userData._id}?organisationId=${userData.organisation._id}`}
              >
                Mon compte
              </GoogleLink>
              " pour le reconnecter. Vous pourrez ainsi envoyer des emails depuis Horizon.
            </Typography>
          ),
        });
      }
    }
  }, [userData]);

  const [settings, settingsV2, amps] = useMemo(() => {
    let returnSettings: FilteredSettingValues & {
      communicationTypeOptions?: { label: string; value: string; active: boolean }[];
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    } & { ampJobStatusForApply?: any; driverLicenses?: any } = {};
    let returnSettingsV2: FilteredSettings = {};
    let amps: IManagementProcess[] = [];

    if (resultInitSettings) {
      returnSettings = returnFilteredSettingValues(resultInitSettings);
      returnSettingsV2 = returnFilteredSettings(settingsDefinitions, resultInitSettings);
      if (resultAmps && Array.isArray(resultAmps?.data)) {
        amps = resultAmps.data.filter((amp) => amp.kind === "job" && amp.jobBoardDefault);
        if (amps.length) {
          returnSettings.ampJobStatusForApply = amps[0].pipes
            .filter((p) => p.isMatching)
            .map((pipe) => {
              // just take the first who don't have actions or just create todo
              const firstStatusNoSoumSoumAction = pipe.status.find(
                (s) =>
                  !s.actions ||
                  (Array.isArray(s.actions) &&
                    (!s.actions.length ||
                      s.actions.includes("create_todo") ||
                      s.actions.includes("change_candidate_status")))
              );
              if (firstStatusNoSoumSoumAction) {
                return {
                  value: firstStatusNoSoumSoumAction.key,
                  label: firstStatusNoSoumSoumAction.name,
                  active: true,
                };
              }
              return null;
            })
            .filter(Boolean);
          returnSettings.ampJobStatusForApply.shift(); // delete first element (nouveau)
        } else {
          returnSettings.ampJobStatusForApply = [];
        }
      }

      returnSettings.communicationTypeOptions = [
        { label: "SMS", value: "SMS", active: true },
        { label: "Appel", value: "Appel", active: true },
        { label: "Email", value: "Email", active: true },
      ];
    }
    return [returnSettings, returnSettingsV2, amps];
  }, [resultInitSettings, resultAmps]);

  //TODO Fix types
  const updateUserFilterViews = (filterViews) =>
    setUser((user) => {
      if (user) {
        return { ...user, filterViews, filterViewsOrder: filterViews.map((filterView) => filterView._id) };
      }
      return user;
    });
  const updateOrganisationFilterViews = (filterViews) =>
    setUser((user) => {
      if (user) {
        return {
          ...user,
          organisation: { ...user.organisation, configuration: { ...user.organisation.configuration, filterViews } },
        };
      }
      return user;
    });

  const updateNotifications = (notifications: INotification[]) => {
    setNotifications(notifications);
  };

  const values = useMemo(
    () => ({
      user,
      updateUserFilterViews,
      updateOrganisationFilterViews,
      /**@deprecated use settingV2*/
      settings,
      settingsV2,
      allAmps: resultAmps?.data,
      amps,
      setUser,
      getUser,
      loadingUserMe,
      hasHiring,
      notifications,
      centerDrawerNotifications: notifications.filter(
        (n) => isBeneficiarySocialApplyNotification(n) || isPrescriptionFilledNotification(n)
      ),
      updateNotifications,
      settingsDefinitions,
      candidateSchemas: resultCandidateSchemas ?? [],
      getCandidateSchemas,
      challenges: resultChallenges || [],
    }),
    [
      user,
      settings,
      settingsV2,
      resultAmps,
      amps,
      getUser,
      loadingUserMe,
      hasHiring,
      resultCandidateSchemas,
      getCandidateSchemas,
      resultChallenges,
      notifications,
    ]
  );
  return values;
};

export const GlobalProvider = ({ children }) => {
  const values = useGlobalProvider();

  if (!values.user || Object.keys(values.settingsV2).length === 0) {
    return (
      <CircularProgress
        data-testid="loadingUser"
        sx={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
      />
    );
  }
  return (
    <Context.Provider value={values as TGlobalContext}>
      <I18nProvider
        value={{
          timeZone: values.user.organisation.configuration.i18n?.timeZone || defaultI18nValue.timeZone,
          phoneCode: values.user.organisation.configuration.i18n?.phoneCode || defaultI18nValue.phoneCode,
        }}
      >
        {children}
      </I18nProvider>
    </Context.Provider>
  );
};
